import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, CircularProgress,
  Alert, Button, TextField, Card, CardContent,
} from '@mui/material';
import {
  ArrowBack, Mic, Stop, PlayArrow, Pause,
  Delete, Save, Close, GraphicEq,
} from '@mui/icons-material';

const API_BASE = 'https://imara-production-91ab.up.railway.app';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' });
}

const MOOD_MAP = {
  1: { emoji: '😞', label: 'Very Low',  color: '#ef4444' },
  2: { emoji: '😔', label: 'Low',       color: '#f97316' },
  3: { emoji: '😐', label: 'Okay',      color: '#eab308' },
  4: { emoji: '🙂', label: 'Good',      color: '#84cc16' },
  5: { emoji: '😊', label: 'Great',     color: '#4ade80' },
};

export default function Voice() {
  const navigate = useNavigate();

  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [bars, setBars] = useState(Array(20).fill(4));

  // Save form
  const [note, setNote] = useState('');
  const [moodScore, setMoodScore] = useState(3);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Entries
  const [entries, setEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Refs
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const barsTimerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    fetchEntries();
    return () => {
      clearInterval(timerRef.current);
      clearInterval(barsTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchEntries() {
    try {
      setLoadingEntries(true);
      const res = await fetch(`${API_BASE}/voice/entries`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setEntries(data);
    } catch {
      // Silently fail — entries just won't show
    } finally {
      setLoadingEntries(false);
    }
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };

      mr.start(100);
      setRecording(true);
      setRecordingTime(0);

      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(t => {
          if (t >= 120) { stopRecording(); return t; } // max 2 min
          return t + 1;
        });
      }, 1000);

      // Animated bars
      barsTimerRef.current = setInterval(() => {
        setBars(Array(20).fill(0).map(() => Math.floor(Math.random() * 28) + 4));
      }, 120);

    } catch {
      setSaveError('Microphone access denied. Please allow microphone in your browser settings.');
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
      clearInterval(barsTimerRef.current);
      setBars(Array(20).fill(4));
    }
  }

  function discardRecording() {
    setAudioBlob(null);
    setAudioUrl(null);
    setNote('');
    setMoodScore(3);
    setRecordingTime(0);
    setSaveError('');
  }

  async function saveRecording() {
    if (!audioBlob) return;
    setSaving(true); setSaveError('');
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-note.webm');

      const url = `${API_BASE}/voice/upload?mood_score=${moodScore}${note ? `&note=${encodeURIComponent(note)}` : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }

      discardRecording();
      await fetchEntries();
    } catch (e) {
      setSaveError(e.message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  function playEntry(entry) {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingId === entry.id) {
      setPlayingId(null);
      return;
    }

    const token = localStorage.getItem('token');
    // Fetch audio with auth header via blob URL
    fetch(`${API_BASE}/voice/entries/${entry.id}/audio`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        setPlayingId(entry.id);
        audio.play();
        audio.onended = () => setPlayingId(null);
      })
      .catch(() => setSaveError('Could not play this entry.'));
  }

  async function deleteEntry(id) {
    setDeletingId(id);
    try {
      await fetch(`${API_BASE}/voice/entries/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      setEntries(prev => prev.filter(e => e.id !== id));
      if (playingId === id && audioRef.current) {
        audioRef.current.pause();
        setPlayingId(null);
      }
    } catch {
      setSaveError('Could not delete entry.');
    } finally {
      setDeletingId(null);
    }
  }

  const currentMood = MOOD_MAP[moodScore];

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628, #0d1f3c, #0a2040)',
      pb: 6, color: '#e8f4f8',
    }}>

      {/* Header */}
      <Box sx={{
        background: 'rgba(255,255,255,0.02)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 3, py: 2,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#7fa8c8', p: 0.5 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>
            Voice Journal
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.72rem' }}>
            Speak your mind — privately 🎙️
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, pt: 3 }}>

        {saveError && (
          <Alert severity="error" onClose={() => setSaveError('')} sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        {/* ── RECORDER ── */}
        {!audioBlob ? (
          <Card sx={{
            background: recording
              ? 'rgba(239,68,68,0.06)'
              : 'rgba(74,222,128,0.04)',
            border: recording
              ? '1px solid rgba(239,68,68,0.3)'
              : '1px solid rgba(74,222,128,0.2)',
            borderRadius: 3, mb: 3,
          }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>

              {/* Waveform bars */}
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '3px', height: 48, mb: 3,
              }}>
                {bars.map((h, i) => (
                  <Box key={i} sx={{
                    width: 4, height: `${h}px`,
                    borderRadius: 2,
                    background: recording
                      ? `rgba(239,68,68,${0.4 + (h / 32) * 0.6})`
                      : 'rgba(74,222,128,0.2)',
                    transition: recording ? 'height 0.1s ease' : 'none',
                  }} />
                ))}
              </Box>

              {/* Timer */}
              <Typography sx={{
                color: recording ? '#ef4444' : '#7fa8c8',
                fontSize: '2rem', fontWeight: 900,
                fontVariantNumeric: 'tabular-nums',
                mb: 1,
              }}>
                {formatTime(recordingTime)}
              </Typography>

              {recording && (
                <Typography sx={{ color: 'rgba(239,68,68,0.7)', fontSize: '0.75rem', mb: 2 }}>
                  Max 2 minutes • {formatTime(120 - recordingTime)} remaining
                </Typography>
              )}

              {!recording && recordingTime === 0 && (
                <Typography sx={{ color: '#7fa8c8', fontSize: '0.82rem', mb: 3 }}>
                  Tap the mic to start recording
                </Typography>
              )}

              {/* Record / Stop button */}
              <Box
                onClick={recording ? stopRecording : startRecording}
                sx={{
                  width: 80, height: 80,
                  borderRadius: '50%',
                  background: recording
                    ? 'rgba(239,68,68,0.15)'
                    : 'rgba(74,222,128,0.12)',
                  border: recording
                    ? '2px solid rgba(239,68,68,0.5)'
                    : '2px solid rgba(74,222,128,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', mx: 'auto',
                  transition: 'all 0.2s ease',
                  boxShadow: recording
                    ? '0 0 24px rgba(239,68,68,0.3)'
                    : '0 0 16px rgba(74,222,128,0.2)',
                  animation: recording ? 'recPulse 1.5s infinite' : 'none',
                  '@keyframes recPulse': {
                    '0%': { boxShadow: '0 0 16px rgba(239,68,68,0.3)' },
                    '50%': { boxShadow: '0 0 32px rgba(239,68,68,0.6)' },
                    '100%': { boxShadow: '0 0 16px rgba(239,68,68,0.3)' },
                  },
                  '&:hover': { transform: 'scale(1.06)' },
                  '&:active': { transform: 'scale(0.96)' },
                }}
              >
                {recording
                  ? <Stop sx={{ color: '#ef4444', fontSize: 36 }} />
                  : <Mic sx={{ color: '#4ade80', fontSize: 36 }} />}
              </Box>

              {recording && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', animation: 'blink 1s infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } } }} />
                  <Typography sx={{ color: '#ef4444', fontSize: '0.78rem', fontWeight: 700 }}>RECORDING</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (

          /* ── SAVE FORM ── */
          <Card sx={{
            background: 'rgba(74,222,128,0.04)',
            border: '1px solid rgba(74,222,128,0.25)',
            borderRadius: 3, mb: 3,
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ color: '#4ade80', fontWeight: 700, fontSize: '0.85rem' }}>
                  ✅ Recording ready — {formatTime(recordingTime)}
                </Typography>
                <IconButton size="small" onClick={discardRecording} sx={{ color: '#7fa8c8' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Preview player */}
              <Box sx={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 2, p: 1.5, mb: 2,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <GraphicEq sx={{ color: '#4ade80', fontSize: 20 }} />
                <audio controls src={audioUrl} style={{ flex: 1, height: 32, filter: 'invert(0.8)' }} />
              </Box>

              {/* Mood picker */}
              <Typography sx={{ color: '#7fa8c8', fontSize: '0.72rem', letterSpacing: '0.1em', mb: 1.5 }}>
                HOW ARE YOU FEELING?
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                {[1,2,3,4,5].map(v => (
                  <Box key={v} onClick={() => setMoodScore(v)} sx={{
                    flex: 1, textAlign: 'center', py: 1,
                    borderRadius: 2, cursor: 'pointer',
                    background: moodScore === v ? `${MOOD_MAP[v].color}22` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${moodScore === v ? MOOD_MAP[v].color + '66' : 'rgba(255,255,255,0.08)'}`,
                    transition: 'all 0.15s',
                  }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>{MOOD_MAP[v].emoji}</Typography>
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: currentMood.color, fontSize: '0.78rem', fontWeight: 700, textAlign: 'center', mb: 2 }}>
                {currentMood.label}
              </Typography>

              {/* Note */}
              <TextField
                fullWidth multiline rows={2}
                placeholder="Add a note to this recording (optional)..."
                value={note}
                onChange={e => setNote(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white', fontSize: '0.88rem',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: '#4ade80' },
                    '&.Mui-focused fieldset': { borderColor: '#4ade80' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.25)' },
                }}
              />

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  onClick={discardRecording}
                  sx={{ color: '#7fa8c8', textTransform: 'none', flex: 1, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}
                >
                  Discard
                </Button>
                <Button
                  onClick={saveRecording}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} sx={{ color: '#0a1628' }} /> : <Save />}
                  sx={{
                    flex: 2,
                    background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
                    color: '#0a1628', fontWeight: 700,
                    textTransform: 'none', borderRadius: 2,
                    '&:hover': { opacity: 0.9 },
                    '&:disabled': { opacity: 0.5 },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Entry'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ── PAST ENTRIES ── */}
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', letterSpacing: '0.12em', mb: 2 }}>
          PAST ENTRIES
        </Typography>

        {loadingEntries && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#4ade80' }} size={28} />
          </Box>
        )}

        {!loadingEntries && entries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🎙️</Typography>
            <Typography sx={{ color: '#7fa8c8', fontSize: '0.88rem' }}>
              No voice entries yet. Record your first one above.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {entries.map(entry => {
            const mood = entry.mood_score ? MOOD_MAP[entry.mood_score] : null;
            const isPlaying = playingId === entry.id;
            return (
              <Box key={entry.id} sx={{
                background: 'rgba(13,31,60,0.85)',
                border: isPlaying
                  ? '1px solid rgba(74,222,128,0.4)'
                  : '1px solid rgba(255,255,255,0.07)',
                borderRadius: 3, p: 2,
                transition: 'border 0.2s',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

                  {/* Play button */}
                  <Box
                    onClick={() => playEntry(entry)}
                    sx={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: isPlaying ? 'rgba(74,222,128,0.15)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isPlaying ? 'rgba(74,222,128,0.4)' : 'rgba(255,255,255,0.1)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': { background: 'rgba(74,222,128,0.1)' },
                    }}
                  >
                    {isPlaying
                      ? <Pause sx={{ color: '#4ade80', fontSize: 22 }} />
                      : <PlayArrow sx={{ color: '#7fa8c8', fontSize: 22 }} />}
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      {mood && (
                        <Typography sx={{ fontSize: '0.9rem' }}>{mood.emoji}</Typography>
                      )}
                      <Typography sx={{ color: '#e8f4f8', fontSize: '0.85rem', fontWeight: 600 }}>
                        {timeAgo(entry.created_at)}
                      </Typography>
                    </Box>
                    {entry.note && (
                      <Typography sx={{
                        color: '#7fa8c8', fontSize: '0.78rem',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {entry.note}
                      </Typography>
                    )}
                    {isPlaying && (
                      <Box sx={{ display: 'flex', gap: '2px', mt: 0.5 }}>
                        {Array(12).fill(0).map((_, i) => (
                          <Box key={i} sx={{
                            width: 3,
                            height: `${Math.random() * 12 + 4}px`,
                            background: '#4ade80',
                            borderRadius: 1,
                            animation: `wave${i % 3} 0.8s infinite alternate`,
                            [`@keyframes wave${i % 3}`]: {
                              from: { height: '4px' },
                              to: { height: `${8 + (i % 4) * 4}px` },
                            },
                          }} />
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Delete */}
                  <IconButton
                    size="small"
                    onClick={() => deleteEntry(entry.id)}
                    disabled={deletingId === entry.id}
                    sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#ef4444' } }}
                  >
                    {deletingId === entry.id
                      ? <CircularProgress size={14} sx={{ color: '#ef4444' }} />
                      : <Delete fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Storage note */}
        <Box sx={{
          mt: 4, p: 2,
          background: 'rgba(245,158,11,0.06)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 2,
        }}>
          <Typography sx={{ color: '#f59e0b', fontSize: '0.72rem', fontWeight: 700, mb: 0.5 }}>
            ⚠️ Beta note
          </Typography>
          <Typography sx={{ color: '#7fa8c8', fontSize: '0.72rem', lineHeight: 1.6 }}>
            Voice entries are stored on the server and may be lost when the app is updated. 
            Cloudinary integration for permanent storage is coming in v1.1.
          </Typography>
        </Box>

      </Box>
    </Box>
  );
}